/**
 * Customer Pricing Tier System
 * 
 * Utilities untuk calculate harga berdasarkan customer pricing tier.
 * Sinkron dengan backend PricingService.
 */

import { PricingTier } from '../services/customerService';

export interface PricingInfo {
  basePrice: number;
  pricingTier: PricingTier;
  discountRate: number;
  discountAmount: number;
  unitPrice: number;
  subtotal: number;
  total: number;
  quantity: number;
  paymentMethod: 'cash' | 'tempo';
  paymentDueDays: number;
  isMarkup: boolean;
  tierDescription: string;
}

export interface PricingCalculationOptions {
  basePrice: number;
  quantity?: number;
  paymentMethod?: 'cash' | 'tempo';
  paymentDueDays?: number;
}

/**
 * Get discount rate based on pricing tier and payment method.
 */
export const getDiscountRate = (
  tier: PricingTier,
  paymentMethod: 'cash' | 'tempo' = 'cash',
  paymentDueDays: number = 0
): number => {
  switch (tier) {
    case PricingTier.NO_DISCOUNT:
      return 0;
    
    case PricingTier.BRONZE:
      return 0.03; // 3%
    
    case PricingTier.SILVER:
      return 0.05; // 5%
    
    case PricingTier.GOLD:
      if (paymentMethod === 'cash') {
        return 0.10; // 10% for cash
      } else if (paymentMethod === 'tempo' && paymentDueDays <= 7) {
        return 0.05; // 5% for tempo max 7 days
      }
      return 0;
    
    case PricingTier.RETAIL_MARKUP:
      return -0.35; // 35% markup (negative = markup)
    
    default:
      return 0;
  }
};

/**
 * Get human-readable description of pricing tier.
 */
export const getTierDescription = (
  tier: PricingTier,
  paymentMethod: 'cash' | 'tempo' = 'cash',
  paymentDueDays: number = 0
): string => {
  switch (tier) {
    case PricingTier.NO_DISCOUNT:
      return 'Harga Normal (Tanpa Diskon)';
    
    case PricingTier.BRONZE:
      return 'Member Bronze - Diskon 3%';
    
    case PricingTier.SILVER:
      return 'Member Silver - Diskon 5%';
    
    case PricingTier.GOLD:
      if (paymentMethod === 'cash') {
        return 'Member Gold - Diskon 10% (Cash)';
      } else if (paymentMethod === 'tempo' && paymentDueDays <= 7) {
        return `Member Gold - Diskon 5% (Tempo ${paymentDueDays} Hari)`;
      }
      return 'Member Gold - Tempo > 7 Hari (Tanpa Diskon)';
    
    case PricingTier.RETAIL_MARKUP:
      return 'Harga Retail +35%';
    
    default:
      return 'Unknown Tier';
  }
};

/**
 * Calculate pricing info for a customer.
 */
export const calculatePricing = (
  tier: PricingTier,
  options: PricingCalculationOptions
): PricingInfo => {
  const {
    basePrice,
    quantity = 1,
    paymentMethod = 'cash',
    paymentDueDays = 0,
  } = options;

  // Validate gold tier tempo
  if (tier === PricingTier.GOLD && paymentMethod === 'tempo' && paymentDueDays > 7) {
    console.warn('Gold tier discount only applies for tempo <= 7 days');
  }

  // Get discount rate
  const discountRate = getDiscountRate(tier, paymentMethod, paymentDueDays);
  const isMarkup = discountRate < 0;

  // Calculate prices
  const subtotal = basePrice * quantity;
  let unitPrice: number;
  let adjustmentAmount: number;

  if (isMarkup) {
    // Markup case
    const markupRate = Math.abs(discountRate);
    unitPrice = basePrice * (1 + markupRate);
    adjustmentAmount = unitPrice - basePrice;
  } else {
    // Discount case
    adjustmentAmount = basePrice * discountRate;
    unitPrice = basePrice - adjustmentAmount;
  }

  const total = unitPrice * quantity;
  const totalAdjustment = adjustmentAmount * quantity;

  return {
    basePrice,
    pricingTier: tier,
    discountRate,
    discountAmount: totalAdjustment,
    unitPrice,
    subtotal,
    total,
    quantity,
    paymentMethod,
    paymentDueDays,
    isMarkup,
    tierDescription: getTierDescription(tier, paymentMethod, paymentDueDays),
  };
};

/**
 * Calculate final price (quick utility).
 */
export const calculateFinalPrice = (
  tier: PricingTier,
  basePrice: number,
  quantity: number = 1,
  paymentMethod: 'cash' | 'tempo' = 'cash',
  paymentDueDays: number = 0
): number => {
  const pricing = calculatePricing(tier, {
    basePrice,
    quantity,
    paymentMethod,
    paymentDueDays,
  });
  return pricing.total;
};

/**
 * Format pricing info for display.
 */
export const formatPricingDisplay = (pricing: PricingInfo): string => {
  const formatter = new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  if (pricing.isMarkup) {
    const markupPercent = Math.abs(pricing.discountRate) * 100;
    return `${formatter.format(pricing.total)} (Markup +${markupPercent}%)`;
  } else if (pricing.discountRate > 0) {
    const discountPercent = pricing.discountRate * 100;
    return `${formatter.format(pricing.total)} (Diskon ${discountPercent}%)`;
  } else {
    return formatter.format(pricing.total);
  }
};

/**
 * Get badge color for pricing tier.
 */
export const getTierBadgeColor = (tier: PricingTier): string => {
  switch (tier) {
    case PricingTier.NO_DISCOUNT:
      return 'gray';
    case PricingTier.BRONZE:
      return 'orange';
    case PricingTier.SILVER:
      return 'blue';
    case PricingTier.GOLD:
      return 'yellow';
    case PricingTier.RETAIL_MARKUP:
      return 'purple';
    default:
      return 'gray';
  }
};

/**
 * Get short label for pricing tier.
 */
export const getTierLabel = (tier: PricingTier): string => {
  switch (tier) {
    case PricingTier.NO_DISCOUNT:
      return 'No Discount';
    case PricingTier.BRONZE:
      return 'Bronze (3%)';
    case PricingTier.SILVER:
      return 'Silver (5%)';
    case PricingTier.GOLD:
      return 'Gold (10%/5%)';
    case PricingTier.RETAIL_MARKUP:
      return 'Retail (+35%)';
    default:
      return 'Unknown';
  }
};

/**
 * Validate gold tier tempo days.
 */
export const validateGoldTempo = (paymentDueDays: number): boolean => {
  return paymentDueDays <= 7;
};

/**
 * Get badge info for pricing tier display.
 * Returns config object for rendering badge component.
 */
export const getPricingTierBadge = (tier: PricingTier) => {
  const badges = {
    [PricingTier.NO_DISCOUNT]: {
      label: 'No Discount',
      className: 'px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-700 rounded',
    },
    [PricingTier.BRONZE]: {
      label: 'Bronze -3%',
      className: 'px-2 py-0.5 text-xs font-semibold bg-orange-100 text-orange-700 rounded',
    },
    [PricingTier.SILVER]: {
      label: 'Silver -5%',
      className: 'px-2 py-0.5 text-xs font-semibold bg-gray-300 text-gray-800 rounded',
    },
    [PricingTier.GOLD]: {
      label: 'Gold -10%',
      className: 'px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded',
    },
    [PricingTier.RETAIL_MARKUP]: {
      label: 'Retail +35%',
      className: 'px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-800 rounded',
    },
  };

  return badges[tier] || badges[PricingTier.NO_DISCOUNT];
};
